"use client";

import {
  fitWindow,
  type GraphLine,
  LinesGraph,
} from "@/components/tools/algebra/lines-graph";
import {
  StepList,
  type WorkedStep,
} from "@/components/tools/algebra/step-list";
import type { SYSTEM_OF_EQUATIONS_SOLVER_STRINGS } from "@/components/tools/system-of-equations-solver";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  checkRow,
  formatRow,
  type ReadSystem,
  type Row,
  type SystemSolution,
  type SystemStep,
} from "@/lib/tool-kit/algebra/system";
import {
  formatStyled,
  isZeroR,
  type NumberStyle,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof SYSTEM_OF_EQUATIONS_SOLVER_STRINGS;

interface Context {
  system: ReadSystem;
  solution: SystemSolution;
  strings: Strings;
  style: NumberStyle;
  mark: string;
}

function systemText(rows: Row[], ctx: Context): string {
  return rows
    .map(
      (row, i) =>
        `E${i + 1}:  ${formatRow(row, ctx.system.variables, ctx.style, ctx.mark)}`
    )
    .join("\n");
}

function combinationText(step: SystemStep, strings: Strings): string {
  const a = step.multiplier ?? BigInt(1);
  const b = step.other ?? BigInt(0);
  const target = t(strings.eq, { n: step.target });
  const source = t(strings.eq, { n: step.source ?? 0 });
  const first = a === BigInt(1) ? target : `${a} × ${target}`;
  const size = b < BigInt(0) ? -b : b;
  const second = size === BigInt(1) ? source : `${size} × ${source}`;
  return `${first} ${b < BigInt(0) ? "−" : "+"} ${second}`;
}

function describe(step: SystemStep, ctx: Context): string {
  const { strings } = ctx;
  const show = (value: Rational | undefined) =>
    value ? formatStyled(value, ctx.style, ctx.mark) : "";
  switch (step.kind) {
    case "scale":
      return t(strings.stepScale, {
        row: t(strings.eq, { n: step.target }),
        factor: show(step.factor),
      });
    case "swap":
      return t(strings.stepSwap, {
        a: t(strings.eq, { n: step.target }),
        b: t(strings.eq, { n: step.source ?? 0 }),
      });
    case "divide":
      return t(strings.stepDivide, {
        row: t(strings.eq, { n: step.target }),
        divisor: show(step.factor),
        variable: step.variable ?? "",
      });
    default: {
      const base = t(strings.stepCombine, {
        variable: step.variable ?? "",
        row: t(strings.eq, { n: step.target }),
        expression: combinationText(step, strings),
      });
      if (!step.reducedBy) {
        return base;
      }
      return `${base} ${t(strings.stepReduce, { divisor: String(step.reducedBy) })}`;
    }
  }
}

function degenerateNote(step: SystemStep, ctx: Context): string | null {
  if (!step.degenerate) {
    return null;
  }
  const row = t(ctx.strings.eq, { n: step.target });
  if (step.degenerate === "true") {
    return t(ctx.strings.stepTrivial, { row });
  }
  const c = step.rows[step.target - 1].at(-1) as Rational;
  return t(ctx.strings.stepFalse, {
    row,
    value: formatStyled(c, ctx.style, ctx.mark),
  });
}

export function EliminationSteps(props: Context) {
  const { system, solution, strings } = props;
  const rows: WorkedStep[] = [
    { text: strings.stepStart, math: systemText(system.rows, props) },
  ];
  for (const step of solution.steps) {
    const note = degenerateNote(step, props);
    rows.push({
      text: describe(step, props),
      math: systemText(step.rows, props),
      highlight: note !== null,
      note,
    });
  }
  return <StepList steps={rows} />;
}

export function CramerSteps({
  system,
  solution,
  strings,
  style,
  mark,
}: Context) {
  const dets = solution.cramer;
  if (!dets) {
    return null;
  }
  const show = (value: Rational) => formatStyled(value, style, mark);
  const [main, ...rest] = dets;
  if (isZeroR(main.value)) {
    return (
      <p className="text-muted-foreground text-sm">{strings.cramerZero}</p>
    );
  }
  const lines = [
    `${main.label} = ${main.expression} = ${show(main.value)}`,
    ...rest.map((d) => `${d.label} = ${d.expression} = ${show(d.value)}`),
    ...rest.map((d, i) =>
      t(strings.cramerResult, {
        variable: system.variables[i],
        numerator: d.label,
        value: show(d.value),
        denominator: show(main.value),
        answer: show(solution.values[i]),
      })
    ),
  ];
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-sm">{strings.cramerIntro}</p>
      <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-foreground text-sm leading-relaxed">
        {lines.join("\n")}
      </pre>
    </div>
  );
}

export function CheckLines({
  system,
  solution,
  strings,
  style,
  mark,
}: Context) {
  return (
    <ul className="space-y-1 font-mono text-foreground text-sm">
      {system.rows.map((row, i) => {
        const check = checkRow(row, solution.values, style, mark);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional equations
          <li key={i}>
            {t(strings.checkLine, {
              row: t(strings.eq, { n: i + 1 }),
              working: check.working,
              total: check.total,
            })}
          </li>
        );
      })}
    </ul>
  );
}

function lineFor(
  row: Row,
  names: readonly string[],
  ctx: Context
): GraphLine | null {
  const [a, b] = row;
  const c = row[2];
  const label = formatRow(row, names, ctx.style, ctx.mark);
  if (!isZeroR(b)) {
    const slope = -toNumberR(a) / toNumberR(b);
    const intercept = toNumberR(c) / toNumberR(b);
    return { fn: (x) => slope * x + intercept, label };
  }
  if (!isZeroR(a)) {
    return { vertical: toNumberR(c) / toNumberR(a), label };
  }
  return null;
}

export function SystemGraph(props: Context) {
  const fmt = useToolNumber();
  const { system, solution, strings } = props;
  const lines = system.rows
    .map((row) => lineFor(row, system.variables, props))
    .filter((line): line is GraphLine => line !== null);
  const fns = lines.flatMap((l) => (l.fn ? [l.fn] : []));
  const verticals = lines.flatMap((l) =>
    l.vertical === undefined ? [] : [l.vertical]
  );
  const unique = solution.outcome === "unique" ? solution.values : null;
  const center = unique ? toNumberR(unique[0]) : (verticals[0] ?? 0);
  const marks = unique
    ? [
        {
          x: toNumberR(unique[0]),
          y: toNumberR(unique[1]),
          label: t(strings.point, {
            x: fmt(toNumberR(unique[0]), { maximumFractionDigits: 3 }),
            y: fmt(toNumberR(unique[1]), { maximumFractionDigits: 3 }),
          }),
        },
      ]
    : [];
  return (
    <LinesGraph
      downloadLabel={strings.downloadPng}
      fileName="system-of-equations-graph.png"
      lines={lines}
      marks={marks}
      title={strings.graphTitle}
      viewport={fitWindow(
        center,
        fns,
        marks.map((m) => m.y)
      )}
    />
  );
}
